import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, ListingCardSkeleton, PaginationLinks } from '../../../components';

import css from './SearchResultsPanel.module.css';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @param {boolean} [props.loading] - Whether a search is in progress (shows placeholder cards)
 * @param {number} [props.placeholderCount] - Number of placeholder cards to render while loading
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    currentPathParams,
    selectedCurrency,
    exchangeRates,
    loading = false,
    placeholderCount = 24,
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  // Determine route name and path params from current category path
  const { cat1, cat2, cat3 } = currentPathParams || {};
  const pageName = cat3 ? 'SearchPageCat3' : cat2 ? 'SearchPageCat2' : cat1 ? 'SearchPageCat1' : 'SearchPage';
  const pagePathParams = {};
  if (cat1) pagePathParams.cat1 = cat1;
  if (cat2) pagePathParams.cat2 = cat2;
  if (cat3) pagePathParams.cat3 = cat3;

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={pagePathParams}
        pageSearchParams={search}
        pagination={pagination}
      />
    ) : null;

  // Compute once — value is the same for every card in the grid.
  // Map variant: listing panel ~50 % of viewport, 2 cols → ~25 vw; 1921 px+ 3 cols → ~17 vw
  // Grid variant (non-map):
  //   Mobile/Tablet (<1024): 2 cols, 16 px gutter × 2 + 12 px gap = 44 px → (VW − 44) / 2
  //   Desktop (1024–1200): 4 cols, 24 px gutter × 2 + 24 px gap × 3 = 120 px → (VW − 120) / 4
  //   Desktop (>1200): content capped at 1200 px → (1200 − 120) / 4 = 270 px
  const renderSizes = isMapVariant
    ? '(max-width: 1920px) 25vw, 17vw'
    : '(max-width: 1023px) calc((100vw - 44px) / 2), (max-width: 1200px) calc((100vw - 120px) / 4), 270px';

  // While a search is in progress (initial load or any filter/search change),
  // fill the grid with placeholder cards so the layout doesn't shift and the
  // user sees the grid take shape immediately. Grid (non-map) view only.
  const showPlaceholders = loading && !isMapVariant;

  return (
    <div className={classes}>
      <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {showPlaceholders
          ? Array.from({ length: placeholderCount }).map((_, index) => (
              <ListingCardSkeleton className={css.listingCard} key={`skeleton-${index}`} />
            ))
          : listings.map((l, index) => (
              <ListingCard
                className={css.listingCard}
                key={l.id.uuid}
                listing={l}
                renderSizes={renderSizes}
                setActiveListing={setActiveListing}
                selectedCurrency={selectedCurrency}
                exchangeRates={exchangeRates}
                index={index}
              />
            ))}
        {showPlaceholders ? null : props.children}
      </div>
      {showPlaceholders ? null : paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
